import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Phone, 
  Clock, 
  MapPin, 
  Award, 
  Wrench, 
  Users, 
  Star, 
  CheckCircle,
  Shield,
  Heart,
  Calendar,
  BookOpen,
  Home,
  GraduationCap,
  Trophy,
  Zap
} from 'lucide-react';

const MeetTheTeam = () => {
  // BirdEye widget loading
  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  // SEO meta tags setup
  useEffect(() => {
    
    const metaTags = [
      { name: 'description', content: 'Meet the Colorado First Plumbing team! Master Plumber Codee Jessen, Office Manager Michelle Jessen, and our skilled technicians. Family-owned, woman-owned business serving Denver Metro since 2018.' },
      { name: 'keywords', content: 'Colorado First Plumbing team, Codee Jessen master plumber, Michelle Jessen owner, family owned plumber Colorado, woman owned business, Denver Metro plumbing team' },
      { property: 'og:title', content: 'Meet Our Team | Colorado First Plumbing | Family-Owned & Master Plumber Led' },
      { property: 'og:description', content: 'Meet the Colorado First Plumbing team! Master Plumber Codee Jessen, Office Manager Michelle Jessen, and our skilled technicians. Family-owned, woman-owned business.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/meet-the-team' },
      { property: 'og:image', content: 'https://colorado-plumbing.com/colorado-plumbing-uploads/brand/Prices-you-can-see-Reviews-you-can-trust.png' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });

    // Structured data for the team page
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'Meet Our Team - Colorado First Plumbing',
      'description': 'Meet the Colorado First Plumbing team! Master Plumber Codee Jessen, Office Manager Michelle Jessen, and our skilled technicians.',
      'mainEntity': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'employee': [
          {
            '@type': 'Person',
            'name': 'Codee Jessen',
            'jobTitle': 'Master Plumber & Owner',
            'description': 'Licensed Colorado Master Plumber with over 10 multifamily projects and 6000+ customers served since 2018.'
          },
          {
            '@type': 'Person',
            'name': 'Michelle Jessen',
            'jobTitle': 'Office Management & Majority Owner',
            'description': 'Majority Owner making Colorado First Plumbing woman-owned. Expert in office management, CRM, and customer care.'
          },
          {
            '@type': 'Person',
            'name': 'Zack',
            'jobTitle': 'Newest Team Member',
            'description': 'High-level experience in water heater and boiler installation. Known for determination, commitment, and positive attitude.'
          },
          {
            '@type': 'Person',
            'name': 'Jesse Sellers',
            'jobTitle': 'Plumbing Technician',
            'description': '3 years plumbing experience in residential and commercial. Background in remodeling, carpentry, and construction. Colorado native with broad mechanical knowledge.'
          }
        ]
      }
    };

    let structuredDataScript = document.querySelector('#team-structured-data');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('id', 'team-structured-data');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    return () => {
      const script = document.querySelector('#team-structured-data');
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const teamMembers = [
    {
      name: "Codee Jessen",
      title: "Master Plumber & Owner",
      route: "/team/codee-jessen",
      image1: "/colorado-plumbing-uploads/team/codee-jessen.png",
      image2: "/colorado-plumbing-uploads/team/codee-jessen-coach.png",
      highlights: [
        "Licensed Colorado Master Plumber",
        "Owner & Lead Technician",
        "6000+ Customers Served Since 2018",
        "10+ Multifamily Projects Completed",
        "Volunteer Soccer Coach"
      ],
      icon: <Award className="w-6 h-6 text-trust-primary" />,
      background: "from-trust-deep to-authority-navy"
    },
    {
      name: "Michelle Jessen",
      title: "Office Management & Majority Owner",
      route: "/team/michelle-jessen", 
      image1: "/colorado-plumbing-uploads/team/womanownedplumbing.png",
      image2: "/colorado-plumbing-uploads/team/michellejessen.png",
      highlights: [
        "Majority Owner (Woman-Owned Business)",
        "Expert Office Management",
        "CRM & Scheduling Specialist",
        "Colorado Native from Westminster",
        "Community Volunteer"
      ],
      icon: <Users className="w-6 h-6 text-success-primary" />,
      background: "from-success-primary to-success-eco"
    },
    {
      name: "Zack",
      title: "Newest Team Member",
      route: "/team/zack",
      image1: "/colorado-plumbing-uploads/team/zack.png", 
      image2: null,
      highlights: [
        "Water Heater Installation Expert",
        "Boiler Installation Specialist", 
        "Milwaukee Tool Collection Owner",
        "Works 2 Full-Time Jobs",
        "110% Commitment to Excellence"
      ],
      icon: <Trophy className="w-6 h-6 text-action-warm" />,
      background: "from-action-warm to-action-primary"
    },
    {
      name: "Jesse Sellers",
      title: "Plumbing Technician",
      route: "/team/jesse-sellers",
      image1: "/colorado-plumbing-uploads/team/jesse-sellers.jpg", 
      image2: null,
      highlights: [
        "3+ Years Plumbing Experience",
        "Residential & Commercial Service",
        "Remodeling & Carpentry Background",
        "Colorado Native",
        "Broad Mechanical Knowledge"
      ],
      icon: <Wrench className="w-6 h-6 text-trust-primary" />,
      background: "from-trust-primary to-authority-navy"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-60 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-trust-primary/20 to-success-primary/20"></div>
        <div className="container-custom relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <Users className="w-12 h-12 text-action-warm mr-4" />
              <span className="px-4 py-2 bg-action-primary/20 rounded-full text-action-warm font-medium text-sm border border-action-primary/30">
                Family-Owned Since 2018
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Meet the Colorado First
              <span className="text-action-warm"> Plumbing Team</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-trust-light mb-8 leading-relaxed">
              Master plumber-owned, woman-owned family business serving the Denver Metro area with 
              <span className="text-action-warm font-semibold"> exceptional expertise and personal care</span>
            </p>
            
            {/* BirdEye Widget */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                <p>Reviews loading... See our 500+ verified reviews on Google and BirdEye.</p>
              </div>
            </div>
            
            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mb-8">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-action-warm">6000+</div>
                <div className="text-sm text-trust-light">Customers Served</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-success-eco">300+</div>
                <div className="text-sm text-trust-light">Sewer Projects</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-action-warm">4.8★</div>
                <div className="text-sm text-trust-light">Average Rating</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-success-eco">20</div>
                <div className="text-sm text-trust-light">Years Experience</div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              
              <a 
                href="#team-members" 
                className="inline-flex items-center px-8 py-4 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 font-medium border border-white/30 hover:border-white/50"
              >
                <Users size={18} className="mr-2" />
                Meet the Team
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              What Makes Our Team Different
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              We're not just a plumbing company - we're your neighbors, committed to serving the Colorado community with integrity and expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-trust-light rounded-xl p-8 border border-trust-primary/20">
              <Shield className="w-12 h-12 text-trust-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-authority-navy mb-4">Master Plumber Owned</h3>
              <p className="text-neutral-gray">
                Led by licensed Master Plumber Codee Jessen with deep expertise in residential, commercial, and multifamily projects across Colorado.
              </p>
            </div>
            
            <div className="text-center bg-success-light rounded-xl p-8 border border-success-primary/20">
              <Heart className="w-12 h-12 text-success-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-authority-navy mb-4">Woman-Owned Business</h3>
              <p className="text-neutral-gray">
                Majority owned by Michelle Jessen, making Colorado First Plumbing an officially woman-owned business with a family-first approach.
              </p>
            </div>
            
            <div className="text-center bg-action-primary/10 rounded-xl p-8 border border-action-primary/20">
              <Users className="w-12 h-12 text-action-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-authority-navy mb-4">Community Focused</h3>
              <p className="text-neutral-gray">
                Active volunteers in Highlands Ranch schools, youth soccer, and Scouts. We're invested in the communities we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Members Section */}
      <section id="team-members" className="py-16 bg-gradient-to-br from-neutral-light to-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Our Professional Team
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Get to know the skilled professionals who make Colorado First Plumbing the most trusted name in Denver Metro plumbing.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-neutral-border overflow-hidden transform hover:-translate-y-2">
                {/* Header with gradient background */}
                <div className={`bg-gradient-to-r ${member.background} p-6 text-white relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 opacity-20">
                    {member.icon}
                  </div>
                  <div className="relative">
                    <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                    <p className="text-white/90 font-medium">{member.title}</p>
                  </div>
                </div>
                
                {/* Team Photos */}
                <div className={`${member.image2 ? 'grid grid-cols-2 gap-1' : ''}`}>
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={member.image1}
                      alt={`${member.name} - ${member.title}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {member.image2 && (
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src={member.image2}
                        alt={`${member.name} - Additional Photo`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <ul className="space-y-2 mb-6">
                    {member.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-success-primary mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-neutral-gray">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a 
                    href={member.route}
                    className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-trust-primary to-trust-deep text-white hover:from-trust-deep hover:to-authority-navy transition-all duration-300 font-medium text-sm shadow-md hover:shadow-lg transform hover:scale-[1.02] w-full justify-center"
                  >
                    Learn More About {member.name.split(' ')[0]}
                    <span className="ml-2">→</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Our Team Section */}
      <section className="py-16 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Work with Our Team?
          </h2>
          <p className="text-xl text-trust-light mb-8 max-w-2xl mx-auto">
            Experience the Colorado First Plumbing difference. Professional service, transparent pricing, and the personal touch of a family-owned business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            
            
          </div>
          
          {/* Emergency Notice */}
          <div className="mt-8 p-4 bg-action-urgent/20 rounded-xl border border-action-urgent/30 max-w-lg mx-auto">
            <div className="flex items-center justify-center mb-2">
              <Clock className="w-5 h-5 text-action-warm mr-2" />
              <span className="text-action-warm font-semibold">Emergency Service Available</span>
            </div>
            <p className="text-sm text-white/90">
              Our team provides emergency plumbing service during business hours (8 AM - 5 PM Mon-Sat). Same-day response when possible.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MeetTheTeam;